import React from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { Pagination } from "@sscs/types";
import {
  Card,
  DataTable,
  hasActiveFilters,
  useFilterSortPagination,
} from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { useCheckPaginationPage, useCurrentOrganization } from "Hooks";
import { useGetUsersListQuery } from "../../services/users.api";
import { FilterMembers, SortMembers } from "../../types";
import MemberRow from "../MemberRow/MemberRow";
import MembersTableActionBar from "../MembersTableActionBar/MembersTableActionBar";
import { useMembersColumns } from "../MembersTableHeader/MembersTableHeader";

const initSort: SortMembers = {
  property: "email",
  reverse: false,
};

const atRestFilter: FilterMembers = {
  email: "",
  role: "ALL",
};

const initPagination: Pagination = {
  currentPage: 1,
  itemsPerPage: 10,
};

type GroupMembersProps = {
  groupId: string;
};

const GroupMembers = ({ groupId }: GroupMembersProps): React.JSX.Element => {
  const currentOrganization = useCurrentOrganization();
  const { columns, permissions } = useMembersColumns(groupId);

  const {
    filter,
    sort,
    pagination,
    handleFilterChange,
    handleSortChange,
    handlePaginationChange,
  } = useFilterSortPagination(atRestFilter, initSort, initPagination);

  const { data, isError, isFetching, isLoading, error } = useGetUsersListQuery(
    currentOrganization
      ? {
          ...filter,
          group: groupId,
          organization: currentOrganization.id,
          sortBy: `${sort.reverse ? "-" : ""}${sort.property}`,
          offset: (pagination.currentPage - 1) * pagination.itemsPerPage,
          limit: pagination.itemsPerPage,
        }
      : skipToken,
  );

  useCheckPaginationPage({
    matchCount: data?.match_count,
    itemLength: data?.users.length,
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    handlePaginationChange,
  });

  return (
    <Card.Card>
      <Card.Title as="h2">Members</Card.Title>
      <Card.Filters
        found={{
          id: `group-members-clear-filters-${groupId}`,
          count: data?.match_count ?? 0,
          initialFilters: atRestFilter,
          activeFilters: filter,
          onClear: () => handleFilterChange(atRestFilter),
        }}
      >
        <MembersTableActionBar
          filter={filter}
          groupId={groupId}
          onFilterChange={handleFilterChange}
        />
      </Card.Filters>
      <DataTable
        chrome="plain"
        columns={columns}
        sort={sort}
        onSortChange={handleSortChange}
        empty={{
          subject: "members",
          filters: {
            active: hasActiveFilters(atRestFilter, filter),
            onReset: () => handleFilterChange(atRestFilter),
          },
        }}
        status={getQueryStatus({
          data,
          isLoading,
          isFetching,
          isError,
          error,
        })}
        pagination={{
          itemCount: data?.match_count ?? 0,
          pagination,
          onChange: handlePaginationChange,
        }}
      >
        {data?.users.map((member) => (
          <MemberRow
            key={member.user_id}
            member={member}
            groupId={groupId}
            permissions={permissions}
          />
        ))}
      </DataTable>
    </Card.Card>
  );
};

export default GroupMembers;
